import DOMPurify from "dompurify";
import MarkdownIt from "markdown-it";
import type React from "react";
import { useEffect, useRef } from "react";
import { markLineGaps } from "@/lib/noteLineGaps";
import {
  decorateVerifyFlags,
  markVerifyFlags,
  type VerifyFlagOptions,
} from "@/lib/verifyFlags";
import {
  replaceVerifyMarkers,
  type VerifyChipOptions,
} from "@/lib/verifyMarkers";

interface FileResultRendererMDProps {
  result: string;
  // Show "[verify]" markers as amber chips (generated notes only).
  verifyChips?: VerifyChipOptions;
  // Review card (VITE_REVIEW_CARD_ENABLED): highlight the value each marker
  // flags instead of showing a chip. Uses verifyChips.offset/interactive.
  verifyFlags?: Omit<VerifyFlagOptions, "interactive">;
  // Called with the marker's index in the whole note when an interactive
  // chip is pressed.
  onVerifyClick?: (index: number) => void;
  // The note view: blank lines shown as written, as on the copied text
  // (lib/noteLineGaps.ts).
  lineGaps?: boolean;
}

const mdParser = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: true,
});

export const FileResultRendererMD: React.FC<FileResultRendererMDProps> = ({
  result,
  verifyChips,
  verifyFlags,
  onVerifyClick,
  lineGaps = false,
}) => {
  // Chip buttons live in the sanitized HTML, so their clicks are delegated
  // from the container.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onVerifyClick) return;
    const target = (event: Event): Element | null =>
      event.target instanceof Element
        ? event.target.closest(
            "button[data-verify-index], [role=button][data-verify-index]",
          )
        : null;
    const onClick = (event: MouseEvent): void => {
      const chip = target(event);
      if (chip) onVerifyClick(Number(chip.getAttribute("data-verify-index")));
    };
    // Highlights are marks with role="button" (so long values wrap with the
    // text): Enter and Space open them like a button.
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const chip = target(event);
      if (!chip || chip.tagName === "BUTTON") return;
      event.preventDefault();
      onVerifyClick(Number(chip.getAttribute("data-verify-index")));
    };
    container.addEventListener("click", onClick);
    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("click", onClick);
      container.removeEventListener("keydown", onKeyDown);
    };
  }, [onVerifyClick]);

  const processHtmlWithLiteralBullets = (): string => {
    const source =
      verifyChips && verifyFlags
        ? markVerifyFlags(result, verifyChips.offset ?? 0)
        : result;
    let rawHtml: string;
    if (lineGaps) {
      const env = {};
      const tokens = mdParser.parse(source, env);
      markLineGaps(tokens, source);
      rawHtml = mdParser.renderer.render(tokens, mdParser.options, env);
    } else {
      rawHtml = mdParser.render(source);
    }

    const parser = new DOMParser();
    const document = parser.parseFromString(rawHtml, "text/html");

    const topLevelUls = Array.from(
      document.body.querySelectorAll(":scope > ul"),
    );
    const topLevelOls = Array.from(
      document.body.querySelectorAll(":scope > ol"),
    );

    topLevelUls.forEach((ul) => {
      ul.querySelectorAll(":scope > li").forEach((li) => {
        // A loose list item's text is in a paragraph: the bullet goes in it,
        // so it does not sit on a line of its own.
        const firstP =
          li.firstElementChild?.tagName === "P" ? li.firstElementChild : null;
        if (firstP) firstP.innerHTML = `• ${firstP.innerHTML}`;
        else li.innerHTML = `• ${li.innerHTML}`;
      });
    });

    topLevelOls.forEach((ol) => {
      ol.querySelectorAll(":scope > li").forEach((li, index) => {
        const firstP = li.querySelector("p");
        if (firstP) {
          firstP.innerHTML = `${index + 1}. ${firstP.innerHTML}`;
        } else {
          li.innerHTML = `${index + 1}. ${li.innerHTML}`;
        }
      });
    });

    // Without line gaps, a paragraph after a list gets a line break. With
    // them, the note's own blank lines do it.
    [
      ...(lineGaps
        ? []
        : document.body.querySelectorAll(":scope > ul, :scope > ol")),
    ].forEach((list) => {
      const nextElement = list.nextElementSibling;
      if (
        nextElement &&
        nextElement.tagName === "P" &&
        !(
          nextElement.childElementCount === 1 &&
          nextElement.firstElementChild?.tagName === "STRONG"
        )
      ) {
        const br = document.createElement("br");
        list.insertAdjacentElement("afterend", br);
      }
    });

    if (verifyChips && verifyFlags)
      decorateVerifyFlags(document.body, {
        ...verifyFlags,
        interactive: verifyChips.interactive,
      });
    if (verifyChips) replaceVerifyMarkers(document.body, verifyChips);

    return DOMPurify.sanitize(document.body.innerHTML);
  };

  return (
    <div
      ref={containerRef}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is sanitized by DOMPurify after all transformations.
      dangerouslySetInnerHTML={{ __html: processHtmlWithLiteralBullets() }}
      className="editor-markdown editor-markdown__viewer"
    />
  );
};
